import { ZoomIn, ZoomOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatZoom } from './document-zoom';

/**
 * The floating − / 100% / + pill shared by every original-document pane (review PDF,
 * review DOCX, fact-audit source), so zoom looks and behaves identically on each.
 * Render it inside a `relative` wrapper that sits OUTSIDE the pane's scroll container,
 * so it stays put while the document scrolls underneath. Clicking the percentage resets.
 */
export function ZoomControls({
  zoom,
  onZoomIn,
  onZoomOut,
  onReset,
  canIn,
  canOut,
  className,
}: {
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onReset: () => void;
  canIn: boolean;
  canOut: boolean;
  className?: string;
}) {
  const btn =
    'inline-flex size-7 items-center justify-center rounded-md text-white hover:bg-white/15 disabled:opacity-35 disabled:hover:bg-transparent';
  return (
    <div
      role="group"
      aria-label="Document zoom"
      className={cn(
        'absolute top-3 right-3 z-10 flex items-center gap-0.5 rounded-lg bg-navy p-0.5 shadow-card',
        className,
      )}
    >
      <button type="button" aria-label="Zoom out" onClick={onZoomOut} disabled={!canOut} className={btn}>
        <ZoomOut className="size-4" aria-hidden />
      </button>
      <button
        type="button"
        aria-label="Reset zoom"
        title="Reset to 100%"
        onClick={onReset}
        className="min-w-[46px] rounded-md px-1.5 py-1 text-[12px] font-semibold tabular-nums text-white hover:bg-white/15"
      >
        {formatZoom(zoom)}
      </button>
      <button type="button" aria-label="Zoom in" onClick={onZoomIn} disabled={!canIn} className={btn}>
        <ZoomIn className="size-4" aria-hidden />
      </button>
    </div>
  );
}
